import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { updateSequenceTemplate, getSequenceTemplateById, deleteSequenceTemplate } from '../../utils/Product';
import { toast } from 'react-toastify';
import { FaTrash } from 'react-icons/fa';
import { GripVertical } from 'lucide-react';
import ConfirmDeleteTemplateModal from './ConfirmDeleteTemplateModal';
import './EditSequenceTemplateModal.css';

const EditSequenceTemplateModal = ({ 
  show, 
  onHide, 
  templateId,
  templateTitle,
  currentSectionOrder = [],
  onApplyTemplate,
  onTemplateUpdated,
  onTemplateDeleted
}) => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Load template data when modal opens
  useEffect(() => {
    if (show && templateId) {
      loadTemplateData();
    }
  }, [show, templateId]);

  const loadTemplateData = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getSequenceTemplateById(templateId);
      const template = response?.data || response;
      
      if (template && template.section_order && Array.isArray(template.section_order)) {
        setSections([...template.section_order]);
      } else {
        setSections([]);
      }
    } catch (error) {
      console.error('Error loading template:', error);
      setError('Failed to load template data');
      toast.error('Failed to load template data');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(sections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSections(items);
  };

  const handleUseCurrentSequence = () => {
    if (!currentSectionOrder || currentSectionOrder.length === 0) {
      toast.error('No current section order available');
      return;
    }
    
    setSections([...currentSectionOrder]);
    toast.success('Current product sequence loaded');
  };

  const handleSave = async () => {
    if (sections.length === 0) {
      setError('Template must have at least one section');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const templateData = {
        section_order: sections,
      };

      await updateSequenceTemplate(templateId, templateData);
      
      toast.success(`Template "${templateTitle || 'Untitled'}" updated successfully!`);
      
      // Update the current product's sequence
      if (onApplyTemplate) {
        onApplyTemplate(sections);
      }
      
      // Notify parent to refresh templates list
      if (onTemplateUpdated) {
        onTemplateUpdated();
      }
      
      onHide();
    } catch (error) {
      console.error('Error updating template:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update template';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteConfirm(false);
    setDeleting(true);
    setError('');

    try {
      await deleteSequenceTemplate(templateId);
      
      toast.success(`Template "${templateTitle || 'Untitled'}" deleted successfully!`);
      
      // Notify parent to refresh templates list
      if (onTemplateDeleted) {
        onTemplateDeleted();
      }
      
      onHide();
    } catch (error) {
      console.error('Error deleting template:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete template';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = () => {
    setSections([]);
    setError('');
    onHide();
  };

  if (!show) return null;

  return (
    <div className="edit-sequence-modal-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className="edit-sequence-modal">
        <div className="edit-sequence-modal-header">
          <h3 className="edit-sequence-modal-title">
            Edit Template: {templateTitle || 'Untitled'}
          </h3>
          <button className="edit-sequence-close-button" onClick={handleClose}>
            &times;
          </button>
        </div>
        
        <div className="edit-sequence-modal-body">
          {error && (
            <div className="edit-sequence-error">
              {error}
            </div>
          )}

          {loading ? (
            <div className="edit-sequence-loading">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading template...</span>
              </Spinner>
            </div>
          ) : (
            <>
              <div className="edit-sequence-actions-bar">
                <button
                  className="edit-sequence-btn-use-current"
                  onClick={handleUseCurrentSequence}
                  disabled={!currentSectionOrder || currentSectionOrder.length === 0}
                  title="Replace template sections with current product sequence"
                >
                  Use Current Product Sequence
                </button>
                <button
                  className="edit-sequence-btn-delete"
                  onClick={handleDeleteClick}
                  disabled={deleting}
                  title="Delete this template"
                >
                  {deleting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FaTrash className="me-2" />
                      Delete Template
                    </>
                  )}
                </button>
              </div>

              <div className="edit-sequence-sections-container">
                <p className="edit-sequence-description">
                  Drag and drop sections to reorder them. Changes will be saved when you click "Save Changes".
                </p>
                
                {sections.length === 0 ? (
                  <div className="edit-sequence-empty">
                    No sections in this template. Click "Use Current Product Sequence" to add sections.
                  </div>
                ) : (
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="edit-sequence-sections">
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="edit-sequence-sections-list"
                        >
                          {sections.map((section, index) => (
                            <Draggable key={`${section}-${index}`} draggableId={`${section}-${index}`} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`edit-sequence-section-item ${
                                    snapshot.isDragging ? 'edit-sequence-dragging' : ''
                                  }`}
                                >
                                  <div
                                    className="edit-sequence-drag-handle"
                                    {...provided.dragHandleProps}
                                  >
                                    <GripVertical size={20} />
                                  </div>
                                  <div className="edit-sequence-section-info">
                                    <span className="edit-sequence-section-number">{index + 1}.</span>
                                    <span className="edit-sequence-section-name">{section}</span>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </div>
            </>
          )}
        </div>
        
        <div className="edit-sequence-modal-footer">
          <button 
            className="edit-sequence-btn-secondary"
            onClick={handleClose}
            disabled={saving || deleting}
          >
            Cancel
          </button>
          <button 
            className="edit-sequence-btn-primary"
            onClick={handleSave}
            disabled={saving || deleting || sections.length === 0}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>

      <ConfirmDeleteTemplateModal
        show={showDeleteConfirm}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        templateTitle={templateTitle}
      />
    </div>
  );
};

export default EditSequenceTemplateModal;

